import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { studioApi } from '@/lib/studioApi';

export const useImageProcessing = editor => {
  const [jobs, setJobs] = useState({});
  const tasks = useRef(new Map());
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; const active = tasks.current; return () => { mounted.current = false; active.forEach(job => { job.cancelled = true; }); }; }, []);
  const cancel = id => {
    const job = tasks.current.get(id);
    if (job) { job.cancelled = true; tasks.current.delete(id); }
    if (mounted.current) setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
  };
  const removeBackground = async id => {
    const object = editor.getObject(id);
    if (!object || object.type !== 'image' || object.locked || !object.assetId || tasks.current.has(id)) return;
    if (object.cutoutAssetId) {
      editor.patchObject(id, { backgroundRemoved: true, imageView: 'engraving' });
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
      if (object.effect && object.effect !== 'none') applyEffect(id, object.effect, object.effectStrength ?? 50, object.cutoutAssetId);
      return;
    }
    const job = { cancelled: false };
    tasks.current.set(id, job);
    setJobs(previous => ({ ...previous, [id]: { pending: true, error: null } }));
    try {
      const result = await studioApi(`/uploads/${object.assetId}/local-cutout`, { method: 'POST', body: {} });
      if (job.cancelled || !mounted.current || !editor.getObject(id)) return;
      editor.patchObject(id, { cutoutAssetId: result.id, backgroundRemoved: true, imageView: 'engraving' });
      toast.success('Hintergrund entfernt. Dein Original bleibt erhalten.');
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
      if (object.effect && object.effect !== 'none') { tasks.current.delete(id); applyEffect(id, object.effect, object.effectStrength ?? 50, result.id); }
    } catch (error) {
      if (job.cancelled || !mounted.current) return;
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: error.message } }));
    } finally {
      if (tasks.current.get(id) === job) tasks.current.delete(id);
    }
  };
  const reset = id => {
    cancel(id); const object = editor.getObject(id);
    if (object?.locked) return;
    editor.patchObject(id, { backgroundRemoved: false, imageView: 'engraving' });
    if (object?.effect && object.effect !== 'none') applyEffect(id, object.effect, object.effectStrength ?? 50, object.assetId);
  };
  // Effects are computed locally on the server from the cutout (if active) or the original; the original is never replaced.
  const applyEffect = async (id, effect, strength, sourceOverride = null) => {
    const object = editor.getObject(id);
    if (!object || object.type !== 'image' || object.locked || !object.assetId) return;
    const existing = tasks.current.get(id); if (existing) existing.cancelled = true;
    if (!effect || effect === 'none') { editor.patchObject(id, { effect: 'none', effectAssetId: null, imageView: 'engraving' }); setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } })); return; }
    const source = sourceOverride || (object.backgroundRemoved && object.cutoutAssetId ? object.cutoutAssetId : object.assetId);
    const job = { cancelled: false };
    tasks.current.set(id, job);
    setJobs(previous => ({ ...previous, [id]: { pending: true, error: null, effect } }));
    try {
      const result = await studioApi(`/uploads/${source}/effect`, { method: 'POST', body: { effect, strength: Math.round(strength ?? 50) } });
      if (job.cancelled || !mounted.current || !editor.getObject(id)) return;
      editor.patchObject(id, { effect, effectStrength: Math.round(strength ?? 50), effectAssetId: result.id, imageView: 'engraving' });
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
    } catch (error) {
      if (job.cancelled || !mounted.current) return;
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: error.message } }));
    } finally {
      if (tasks.current.get(id) === job) tasks.current.delete(id);
    }
  };
  // KI-Schärfung: replaces the photo with a 2–4× Real-ESRGAN version, then redoes cutout / effect on it.
  const upscale = async id => {
    const object = editor.getObject(id);
    if (!object || object.type !== 'image' || object.locked || !object.assetId || tasks.current.has(id)) return;
    const job = { cancelled: false };
    tasks.current.set(id, job);
    setJobs(previous => ({ ...previous, [id]: { pending: true, error: null, upscale: true } }));
    try {
      const result = await studioApi(`/uploads/${object.assetId}/upscale`, { method: 'POST', body: {} });
      if (job.cancelled || !mounted.current || !editor.getObject(id)) return;
      editor.patchObject(id, { assetId: result.id, upscaled: true, cutoutAssetId: null, effectAssetId: null, backgroundRemoved: false });
      tasks.current.delete(id);
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
      toast.success('Bild mit KI geschärft – jetzt in deutlich höherer Auflösung.');
      if (object.backgroundRemoved) removeBackground(id); else if (object.effect && object.effect !== 'none') applyEffect(id, object.effect, object.effectStrength ?? 50);
    } catch (error) {
      if (job.cancelled || !mounted.current) return;
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: error.message } }));
      toast.error(error.message);
    } finally {
      if (tasks.current.get(id) === job) tasks.current.delete(id);
    }
  };
  // Kristall-Boost: always computed from the original; AI restoration runs as a server job that is polled.
  const crystalBoost = async (id, strength, { cutout = false } = {}) => {
    const object = editor.getObject(id);
    if (!object || object.type !== 'image' || object.locked || !object.assetId || tasks.current.has(id)) return;
    const source = object.crystalBoost?.originalId || object.assetId, job = { cancelled: false };
    tasks.current.set(id, job);
    setJobs(previous => ({ ...previous, [id]: { pending: true, error: null, boost: true } }));
    try {
      let reply = await studioApi(`/uploads/${source}/crystal-boost`, { method: 'POST', body: { strength } });
      for (let tries = 0; reply.status === 'pending' && tries < 120 && !job.cancelled; tries++) {
        await new Promise(resolve => setTimeout(resolve, 2500));
        const jobId = reply.job; reply = { ...(await studioApi(`/boost-jobs/${jobId}`)), job: jobId };
      }
      if (job.cancelled || !mounted.current || !editor.getObject(id)) return;
      if (reply.status !== 'done') throw new Error(reply.error || 'Die Optimierung dauert zu lange – bitte erneut versuchen.');
      const r = reply.result, current = editor.getObject(id);
      editor.patchObject(id, { assetId: r.id, crystalBoost: { strength, originalId: source, ai: r.ai, faces: r.faces }, cutoutAssetId: null, effectAssetId: null, backgroundRemoved: false, imageView: 'engraving' });
      tasks.current.delete(id);
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: null } }));
      if (r.note) toast.message(r.note); else toast.success(r.ai ? 'Für Kristall optimiert – Gesichter mit KI restauriert.' : 'Für Kristall optimiert.');
      if (cutout || current.backgroundRemoved) removeBackground(id);
    } catch (error) {
      if (job.cancelled || !mounted.current) return;
      setJobs(previous => ({ ...previous, [id]: { pending: false, error: error.message, boost: false } }));
      toast.error(error.message);
      if (cutout) { tasks.current.delete(id); removeBackground(id); }
    } finally {
      if (tasks.current.get(id) === job) tasks.current.delete(id);
    }
  };
  const crystalBoostOff = id => {
    const object = editor.getObject(id);
    if (!object?.crystalBoost || object.locked) return;
    cancel(id);
    editor.patchObject(id, { assetId: object.crystalBoost.originalId, crystalBoost: null, cutoutAssetId: null, effectAssetId: null, backgroundRemoved: false, imageView: 'engraving' });
    if (object.backgroundRemoved) setTimeout(() => removeBackground(id), 0);
  };
  const previews = useRef(new Map());
  const effectPreviews = async (source, kind = 'engraving') => {
    if (!source) return {};
    const key = `${kind}:${source}`;
    if (!previews.current.has(key)) previews.current.set(key, studioApi(`/uploads/${source}/effect-previews?kind=${kind}`, { method: 'POST', body: {} }).then(d => d.previews).catch(() => { previews.current.delete(key); return {}; }));
    return previews.current.get(key);
  };
  return { jobs, removeBackground, cancel, reset, applyEffect, effectPreviews, upscale, crystalBoost, crystalBoostOff };
};
